import React, { useEffect, useState } from 'react';

import './styles.css'

const DragDirections = {
    UP: 0,
    DOWN: 1
}

const determineDragDirection = (event, dragBoundaries) => {
    if (dragBoundaries.top !== null && event.clientY < dragBoundaries.top) {
        return DragDirections.UP
    }
    else if (dragBoundaries.bottom !== null && event.clientY > dragBoundaries.bottom) {
        return DragDirections.DOWN
    }
    else {
        return null;
    }
}

const calculateDragBoundaries = (listElements, oldBoundaries, currentIndex, dragDirection = null) => {
    let boundaries = { top: null, bottom: null }

    // If not the first element, set top boundary
    if (currentIndex > 0) {
        boundaries.top = listElements[currentIndex - 1].ref.current.getBoundingClientRect().bottom
    }

    // If not the last element, set bottom boundary
    if (currentIndex < listElements.length - 1) {
        boundaries.bottom = listElements[currentIndex + 1].ref.current.getBoundingClientRect().top
    }

    // If dragDirection is set, we're moving. Update boundaries accordingly
    if (dragDirection === DragDirections.UP) {
        boundaries.bottom = oldBoundaries.top
    }
    else if (dragDirection === DragDirections.DOWN) {
        boundaries.top = oldBoundaries.bottom
    }

    return boundaries
}

const getPlaceholderStyle = (listElements, index, drag) => {
    if (drag && listElements.length > 0) {
        let rect = listElements[index].ref.current.getBoundingClientRect()
        return {
            height: rect.height - 2,
            width: rect.width - 2
        }
    }
    return null;
}

const ReorderableList = ({ children, onReorder }) => {
    const [mouseY, setMouseY] = useState(null)
    const [drag, setDrag] = useState(false)
    const [dragIndex, setDragIndex] = useState(-1)
    const [yOffset, setYOffsett] = useState(null)
    const [dragBoundaries, setDragBoundaries] = useState({ top: null, bottom: null })
    const [listElements, setListElements] = useState([])
    const [order, setOrder] = useState([])

    const listRef = React.useRef()

    useEffect(() => {
        // Bind the event listener
        document.addEventListener("mouseup", handleMouseUpOutside)
        return () => {
            // Unbind the event listener on clean up
            document.removeEventListener("mouseup", handleMouseUpOutside)
        }
    }, [])

    useEffect(() => {
        let elements = children.map((child, index) => {
            return {
                element: child,
                ref: React.createRef(),
                initialIndex: index
            }
        })
        setListElements(elements)

        // Initialise newOrder array with numbers from 0 to children.length
        let newOrder = [...Array(children.length).keys()]
        setOrder(newOrder)
    }, [children])

    const swapElements = (index, newIndex) => {
        let newElements = listElements.slice(0)
        let buffer = listElements[newIndex]
        newElements[newIndex] = listElements[index]
        newElements[index] = buffer
        setListElements(newElements)
    }

    const handleDragging = (e) => {

        const direction = determineDragDirection(e, dragBoundaries)

        switch (direction) {
            case DragDirections.UP:
                swapElements(dragIndex, dragIndex - 1)

                setDragBoundaries(calculateDragBoundaries(listElements, dragBoundaries, dragIndex - 1, direction))

                setDragIndex(dragIndex - 1)
                break
            case DragDirections.DOWN:
                swapElements(dragIndex, dragIndex + 1)

                setDragBoundaries(calculateDragBoundaries(listElements, dragBoundaries, dragIndex + 1, direction))

                setDragIndex(dragIndex + 1)
                break
            default:
                // do nothing
        }
    }

    const handleMouseMove = (e) => {
        setMouseY(e.clientY)

        if (drag) {
            handleDragging(e)
        }
    }

    const handleHandleDown = (index) => {
        if (listElements.length > 0) {
            let rect = listElements[index].ref.current.getBoundingClientRect()
            setDrag(true)
            setDragIndex(index)
            setYOffsett(mouseY - rect.top)
            setDragBoundaries(calculateDragBoundaries(listElements, dragBoundaries, index))
        }
    }

    const updateOrder = () => {
        var newOrder = listElements.map(element => element.initialIndex)
        if (JSON.stringify(newOrder) !== JSON.stringify(order)) {
            onReorder(newOrder)
            setOrder(newOrder)
        }
    }

    const stopDrag = () => {
        setDrag(false)
        setDragIndex(-1)
        setYOffsett(null)
        setDragBoundaries({ top: null, bottom: null })

        updateOrder()
    }

    function handleMouseUpOutside(event) {
        if (!listRef.current.contains(event.target)) {
            stopDrag()
        }
    }

    return <div
        className={'reorderable-list' + (drag ? ' dragged' : '')}
        onMouseMove={handleMouseMove}
        onMouseUp={stopDrag}
        ref={listRef}
    >
        {
            listElements.map(
                (child, index) => {
                    if (index === dragIndex) {
                        return (
                            <div className='reorderable-item-outer'>
                                <div className='reorderable-item dragged'
                                    ref={child.ref}
                                    style={{
                                        position: "fixed",
                                        top: mouseY - yOffset - 10
                                    }}
                                >
                                    <div
                                        className='reorderable-item-handle'
                                        onMouseDown={() => handleHandleDown(index)}
                                    />
                                    {child.element}
                                </div>
                                <div
                                    className='reorderable-item-placeholder dragged'
                                    style={getPlaceholderStyle(listElements, index, drag)}
                                />
                            </div>
                        )
                    }
                    else {
                        return (
                            <div className='reorderable-item-outer'>
                                <div className='reorderable-item'
                                    ref={child.ref}
                                >
                                    <div
                                        className='reorderable-item-handle'
                                        onMouseDown={() => handleHandleDown(index)}
                                    />
                                    {child.element}
                                </div>
                                <div
                                    className='reorderable-item-placeholder'
                                    style={getPlaceholderStyle(listElements, index, drag)}
                                />
                            </div>
                        )
                    }
                }
            )
        }
    </div>
}

export default ReorderableList
